import { call, put, select, takeEvery } from 'redux-saga/effects';
import { replace } from 'connected-react-router';
import {
  currentUserSuccess,
  currentUserFailure,
  currentUserDealersSuccess,
  currentUserDealersFailure,
  currentUserDealerRequest,
  currentUserDealerSuccess,
  currentUserDealerFailure,
  currentUserDealersRequest,
  usersSuccess,
  usersFailure,
  userSuccess,
  userFailure,
  createUserSuccess,
  createUserFailure,
  updateUserSuccess,
  updateUserFailure,
  updateUserDealersSuccess,
  updateUserDealersFailure,
  unlockUserSuccess,
  unlockUserFailure,
  updateUserPasswordSuccess,
  updateUserPasswordFailure,
  deleteUserSuccess,
  deleteUserFailure,
} from './actions';
import { logout } from '../auth/base-sagas';
import AuthService from '../../services/AuthService';
import {
  CURRENT_USER_REQUEST,
  CURRENT_USER_DEALER_REQUEST,
  CURRENT_USER_DEALERS_REQUEST,
  USERS_REQUEST,
  USER_REQUEST,
  CREATE_USER_REQUEST,
  UPDATE_USER_REQUEST,
  UNLOCK_USER_REQUEST,
  UPDATE_USER_PASSWORD_REQUEST,
  DELETE_USER_REQUEST,
  UPDATE_USER_DEALERS_REQUEST,
} from './actionTypes';
import UserService from '../../services/UserService';
import DealerService from '../../services/DealerService';
import { currentUserPermissionsRequest } from '../permissions/actions';
import { getCurrentUser as getCurrentUserSelector } from './selectors';
import { logoutRequest } from '../auth/actions';

function* getCurrentUserHelper() {
  const user = yield call(AuthService.getCurrentUser);
  yield put(currentUserSuccess(user));
  yield put(currentUserPermissionsRequest());
  if (user.dealer_id) {
    yield put(currentUserDealerRequest());
    yield put(currentUserDealersRequest());
  }
  return user;
}

const sortDealerByName = (a, b) => {
  const aName = a.name.toLowerCase();
  const bName = b.name.toLowerCase();

  if (aName > bName) return 1;
  if (aName < bName) return -1;
  return 0;
};

export function* getCurrentUser() {
  try {
    const user = yield call(getCurrentUserHelper);
    return user;
  } catch (error) {
    if (error.status) {
      yield put(currentUserFailure(error));
      yield put(replace('/login'));
      return null;
    }
    // retry request if it was prematurely canceled.
    const user = yield call(getCurrentUserHelper);
    return user;
  }
}

export function* getCurrentUserDealerTask() {
  try {
    const user = yield select(getCurrentUserSelector);
    const dealer = yield call(DealerService.getDealer, user.dealer_id);
    yield put(currentUserDealerSuccess(dealer));
  } catch (error) {
    yield put(currentUserDealerFailure(error));
  }
}

export function* getCurrentUserDealers() {
  try {
    const dealers = yield call(UserService.getDealers);
    const sortedDealer = dealers.sort(sortDealerByName);
    yield put(currentUserDealersSuccess(sortedDealer));
  } catch (error) {
    yield put(currentUserDealersFailure(error));
    yield call(logout);
  }
}

export function* getUsersTask(action) {
  const params = action.payload;
  try {
    const list = yield call(UserService.getUsers, params);
    yield put(usersSuccess(list));
  } catch (error) {
    yield put(usersFailure(error));
  }
}

export function* getUserTask(action) {
  try {
    const userId = action.payload;
    const user = yield call(UserService.getUser, userId);
    yield put(userSuccess(user));
  } catch (error) {
    yield put(userFailure(error));
  }
}

export function* createUserTask(action) {
  try {
    const params = action.payload;
    const user = yield call(UserService.createUser, params);
    yield put(createUserSuccess(user));
    yield put(replace(`/${params.dealer_id}/users`));
  } catch (error) {
    yield put(createUserFailure(error));
  }
}

export function* updateUserTask({ payload }) {
  const { user, redirectTo = `/${payload.dealer_id}/users` } = payload;
  try {
    const updatedUser = yield call(UserService.updateUser, user);
    yield put(updateUserSuccess(updatedUser));
    yield put(replace(redirectTo));
  } catch (error) {
    yield put(updateUserFailure(error));
  }
}

export function* updateUserDealersTask(action) {
  try {
    const { user, dealers, redirectTo } = action.payload;
    yield call(UserService.updateUserDealers, user, dealers);
    yield put(updateUserDealersSuccess());
    yield put(replace(redirectTo));
  } catch (error) {
    yield put(updateUserDealersFailure(error));
  }
}

export function* unlockUserTask(action) {
  try {
    const params = action.payload;
    yield call(UserService.unlockUser, params);
    yield put(unlockUserSuccess());
  } catch (error) {
    yield put(unlockUserFailure(error));
  }
}

export function* updateUserPasswordTask(action) {
  try {
    const params = action.payload;
    yield call(UserService.updateUserPassword, params);
    yield put(updateUserPasswordSuccess());
    // if the user changed their own password, do a full reload and go to the home page to clear out the (now invalid) access token
    const currentUser = yield select(getCurrentUserSelector);
    if (params.email === currentUser.email) {
      yield put(logoutRequest());
    } else {
      yield put(replace(`/${params.dealer_id}/users`));
    }
  } catch (error) {
    yield put(updateUserPasswordFailure(error));
  }
}

export function* deleteUserTask(action) {
  try {
    const userId = action.payload;
    yield call(UserService.deleteUser, userId);
    yield put(deleteUserSuccess(userId));
  } catch (error) {
    yield put(deleteUserFailure(error));
  }
}

export function* userSaga() {
  yield takeEvery(CURRENT_USER_REQUEST, getCurrentUser);
  yield takeEvery(CURRENT_USER_DEALERS_REQUEST, getCurrentUserDealers);
  yield takeEvery(CURRENT_USER_DEALER_REQUEST, getCurrentUserDealerTask);
  yield takeEvery(USERS_REQUEST, getUsersTask);
  yield takeEvery(USER_REQUEST, getUserTask);
  yield takeEvery(CREATE_USER_REQUEST, createUserTask);
  yield takeEvery(UPDATE_USER_REQUEST, updateUserTask);
  yield takeEvery(UNLOCK_USER_REQUEST, unlockUserTask);
  yield takeEvery(UPDATE_USER_PASSWORD_REQUEST, updateUserPasswordTask);
  yield takeEvery(DELETE_USER_REQUEST, deleteUserTask);
  yield takeEvery(UPDATE_USER_DEALERS_REQUEST, updateUserDealersTask);
}
